/**
 * MSAL configuration. One file, one app registration.
 *
 * Replaces six divergent authConfig files (each app had its own, all six
 * byte-different, with the MSAL library itself split across a full major
 * version) and the nine per-module enterprise app registrations they probed.
 *
 * Runtime config comes from /config.js, which the API generates from
 * environment variables — so the same built bundle runs in every environment.
 */
declare global {
  interface Window {
    ERP_CONFIG?: {
      azureClientId: string;
      azureTenantId: string;
      apiScope: string;
    };
  }
}

import { LogLevel, type Configuration } from '@azure/msal-browser';

function runtimeConfig() {
  const config = window.ERP_CONFIG;
  if (!config?.azureClientId || !config.azureTenantId) {
    throw new Error('ERP_CONFIG is missing — /config.js did not load');
  }
  return config;
}

export function msalConfig(): Configuration {
  const { azureClientId, azureTenantId } = runtimeConfig();
  return {
    auth: {
      clientId: azureClientId,
      authority: `https://login.microsoftonline.com/${azureTenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error('[msal]', message);
        },
      },
    },
  };
}

/**
 * Scopes for signing in. Deliberately minimal — the legacy setup hit
 * admin-consent loops by requesting Graph scopes the tenant had not consented
 * to. Group membership now comes from the token's `groups` claim (or Graph
 * server-side on overflow), not from a browser-side Graph call.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Scopes used to obtain the credential we send to our own API.
 *
 * The app registration exposes no API — it has no Application ID URI and no
 * scopes — so `api://<clientId>/.default` does not resolve and Entra rejects it
 * with AADSTS500011 ("resource principal not found"). Exposing one needs an
 * Entra directory role; subscription Owner is not enough.
 *
 * Until that is done we send the **ID token**, which is sound here because the
 * SPA and the API are the same app registration:
 *
 *   - it is signed by Entra and validated against the tenant JWKS
 *   - its `aud` is the client ID, which is exactly what the API expects
 *   - it carries oid, preferred_username and groups — everything role
 *     resolution needs
 *
 * The proper end state is a real access token. Once an admin sets the
 * Application ID URI to `api://<clientId>` and exposes an `access_as_user`
 * scope, change this to that scope and switch the client back to
 * `result.accessToken`. Both sides already accept it: the API validates
 * `aud` against `api://<clientId>` as well as the bare client ID.
 */
export const apiRequest = { scopes: ['openid', 'profile', 'email'] };
